import React, { useEffect, useState } from "react";
import { X, Download, FileText as FileIcon, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function DocumentViewer({ doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!doc) return;
    setLoading(true);
    setErr(null);
    api.get(`/files/${doc.storage_path}`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      })
      .catch(() => setErr("Could not load this file."))
      .finally(() => setLoading(false));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line
  }, [doc]);

  if (!doc) return null;

  const isImage = (doc.content_type || "").startsWith("image/");
  const isPdf = (doc.content_type || "") === "application/pdf";

  const downloadFile = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = doc.filename;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-3 sm:p-6 grid place-items-center" onClick={onClose} data-testid="document-viewer">
      <div className="bg-[#FDFCFB] rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E5E2DC]">
          <FileIcon size={18} className="text-[#1E4D3B]" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[#121C17] truncate" data-testid="viewer-filename">{doc.filename}</div>
            <div className="text-xs text-[#5C6661]">{(doc.size / 1024).toFixed(1)} KB · {doc.content_type}</div>
          </div>
          <button onClick={downloadFile} disabled={!blobUrl} data-testid="viewer-download"
            className="rounded-full px-3 py-1.5 text-xs font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC] inline-flex items-center gap-1 disabled:opacity-50">
            <Download size={12} /> Download
          </button>
          <button onClick={onClose} data-testid="viewer-close" className="rounded-full p-2 hover:bg-[#F3F1EC]" aria-label="Close"><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto bg-[#1B1F1E]">
          {loading && (
            <div className="h-full grid place-items-center text-white/70 p-12">
              <Loader2 size={28} className="animate-spin" />
            </div>
          )}
          {err && !loading && (
            <div className="p-12 text-center text-white/80">{err}</div>
          )}
          {!loading && !err && blobUrl && (
            <>
              {isImage && (
                <img src={blobUrl} alt={doc.filename} className="w-full h-auto object-contain" data-testid="viewer-image" />
              )}
              {isPdf && (
                <iframe src={blobUrl} title={doc.filename} className="w-full h-[80vh] bg-white" data-testid="viewer-pdf" />
              )}
              {!isImage && !isPdf && (
                <div className="p-12 text-center text-white/80">
                  Preview not supported for this file type. Use the Download button.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
